import React from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  style,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const buttonStyle: React.CSSProperties = {
    ...styles.base,
    ...styles[size],
    ...styles[variant],
    ...(isDisabled ? styles.disabled : {}),
    ...(fullWidth ? { width: '100%' } : {}),
    ...style,
  };

  return (
    <button
      style={buttonStyle}
      disabled={isDisabled}
      {...props}
    >
      {loading ? '처리 중...' : children}
    </button>
  );
};

// Button Group Component
interface ButtonGroupProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  gap?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  align = 'left',
  gap = 'md',
  style,
}) => {
  const alignMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  const gapMap = {
    sm: spacing[2],
    md: spacing[3],
    lg: spacing[5],
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: alignMap[align],
        gap: gapMap[gap],
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: typography.fontWeight.medium,
    border: '1px solid',
    borderRadius: '0',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    outline: 'none',
  },
  // Sizes
  sm: {
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
    minWidth: '60px',
  },
  md: {
    padding: `${spacing[2]} ${spacing[5]}`,
    fontSize: typography.fontSize.base,
    minWidth: '80px',
  },
  lg: {
    padding: `${spacing[3]} ${spacing[7]}`,
    fontSize: typography.fontSize.md,
    minWidth: '100px',
  },
  // Variants
  primary: {
    backgroundColor: colors.black,
    color: colors.white,
    borderColor: colors.black,
  },
  secondary: {
    backgroundColor: colors.white,
    color: colors.black,
    borderColor: colors.gray[300],
  },
  danger: {
    backgroundColor: colors.status.error,
    color: colors.white,
    borderColor: colors.status.error,
  },
  warning: {
    backgroundColor: colors.status.warning,
    color: colors.white,
    borderColor: colors.status.warning,
  },
  ghost: {
    backgroundColor: 'transparent',
    color: colors.gray[600],
    borderColor: 'transparent',
  },
  // States
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

export default Button;
